import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://iso27001kit.com";

interface PageSEOOptions {
  title: string;
  description: string;
  canonicalPath?: string; // override pathname if needed
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
  ogAuthor?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleSection?: string;
  articleTags?: string[];
}

export const usePageSEO = ({
  title,
  description,
  canonicalPath,
  ogType = "website",
  ogImage = `${BASE_URL}/og-image.png`,
  noindex = false,
  ogAuthor,
  articlePublishedTime,
  articleModifiedTime,
  articleSection,
  articleTags,
}: PageSEOOptions) => {
  const location = useLocation();

  useEffect(() => {
    // Title
    document.title = title;

    // Meta description
    setMeta("description", description);

    // Robots
    setMeta("robots", noindex ? "noindex,nofollow" : "index,follow");

    // Canonical
    const path = canonicalPath ?? location.pathname;
    setLink("canonical", `${BASE_URL}${path}`);

    // Open Graph
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:url", `${BASE_URL}${path}`, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:site_name", "ISO27001KIT", "property");

    // OG Author & Article metadata
    if (ogAuthor) {
      setMeta("og:author", ogAuthor, "property");
    }
    if (articlePublishedTime) {
      setMeta("article:published_time", articlePublishedTime, "property");
    }
    if (articleModifiedTime) {
      setMeta("article:modified_time", articleModifiedTime, "property");
    }
    if (articleSection) {
      setMeta("article:section", articleSection, "property");
    }
    if (articleTags && articleTags.length) {
      // Replace any existing article:tag entries
      document.querySelectorAll('meta[property="article:tag"]').forEach((n) => n.remove());
      articleTags.forEach((tag) => {
        const m = document.createElement("meta");
        m.setAttribute("property", "article:tag");
        m.content = tag;
        document.head.appendChild(m);
      });
    }

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);

    // Hreflang (English-only site)
    document
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((n) => {
        const lang = n.getAttribute("hreflang");
        if (lang && lang !== "en" && lang !== "x-default") n.remove();
      });
    setLink("alternate", `${BASE_URL}${path}`, "en", "hreflang");
    setLink("alternate", `${BASE_URL}${path}`, "x-default", "hreflang");

    // Content language
    setMeta("content-language", "en");
    setMeta("og:locale", "en_US", "property");
    if (document.documentElement.lang !== "en") {
      document.documentElement.lang = "en";
    }
  }, [title, description, canonicalPath, ogType, ogImage, noindex, ogAuthor, articlePublishedTime, articleModifiedTime, articleSection, articleTags, location.pathname]);
};

function setMeta(nameOrProperty: string, content: string, attr: "name" | "property" = "name") {
  let el = document.querySelector(`meta[${attr}="${nameOrProperty}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, nameOrProperty);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(rel: string, href: string, lang?: string, attrName?: string) {
  const selector = lang && attrName
    ? `link[rel="${rel}"][${attrName}="${lang}"]`
    : `link[rel="${rel}"]`;
  
  let el = document.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (lang && attrName) el.setAttribute(attrName, lang);
    document.head.appendChild(el);
  }
  el.href = href;
}
